<template>
  <div class="add-shop-working-hours">
    <v-modal
      animation="slide-up"
      :minWidth="computedMinWidth"
      :full="!screen.breakPoint.tabPort"
      v-model="showModal"
      handle-click-bg
    >
      <template v-slot:activator="{ on }">
        <slot name="activator" :on="on"></slot>
      </template>

      <template #header>
        {{ $t('components.Profile.addShop.workingHours') }}
      </template>

      <div class="add-shop-working-hours__options-block">
        <div class="add-shop-working-hours__options-item" v-for="(w, index) of computedWorkingHours" :key="index">
          <transition-group name="slide-top">
            <date-input
              @input="(e) => onDateInput(index, e)"
              v-model="w.date"
              v-show="w.weekDay === null"
              future-date
              :placeholder="$t('components.Profile.addShop.specificDate')"
              :value="w.date"
              :is24hr="$i18n.locale !== 'en'"
              :key="'1-g'"
              :error="localDataErrors[index] ? localDataErrors[index].date : null"
            ></date-input>

            <autocomplete
              v-show="w.date === null"
              :value="w.weekDay"
              :items="weekDays"
              item-text="label"
              :value-field="'value'"
              @input="(e) => onWeekDayInput(index, e)"
              :key="'2-g'"
              :placeholder="$t('components.Profile.addShop.weekDay')"
              :error="localDataErrors[index] ? localDataErrors[index].weekDay : null"
            ></autocomplete>

            <date-input
              :mode="'time'"
              :placeholder="$t('components.Profile.addShop.startTime')"
              :value="w.workStartMin"
              :headerText="$t('components.Profile.addShop.startTime')"
              :buttonText="$t('widgets.ConfirmModal.confirm')"
              @input="(e) => onWorkStartMinInput(index, e)"
              :key="'3-g'"
              :error="localDataErrors[index] ? localDataErrors[index].workStartMin : null"
              :is24hr="$i18n.locale !== 'en'"
            ></date-input>

            <date-input
              :mode="'time'"
              :placeholder="$t('components.Profile.addShop.endTime')"
              :value="w.workEndMin"
              :headerText="$t('components.Profile.addShop.endTime')"
              :buttonText="$t('widgets.ConfirmModal.confirm')"
              @input="(e) => onWorkEndMinInput(index, e)"
              :key="'4-g'"
              :error="localDataErrors[index] ? localDataErrors[index].workEndMin : null"
              :is24hr="$i18n.locale !== 'en'"
            ></date-input>

            <v-button
              color="error"
              :key="'5-g'"
              :style="{width: screen.breakPoint.tabLand ? '2rem' : '100%'}"
              @click="removeItem(index)">
              <i class="icon-trash text--xx-large"></i>
            </v-button>
          </transition-group>
        </div>
      </div>

      <div class="d-flex justify-center">
        <v-button size="large" outline rounded color="secondary" @click="addWorkingHour()">
          <span class="icon-plus text--xx-large"></span>
        </v-button>
      </div>

      <template #footer>
        <v-button width="full" @click="save()">{{ $t('components.DatePicker.footer') }}</v-button>
      </template>
    </v-modal>
  </div>
</template>

<script>
import vModal from '@/ui/modals/vModal'

import DateInput from '@/ui/controls/DateInput'
import vButton from '@/ui/controls/vButton'
import Autocomplete from '@/ui/controls/Autocomplete'

import screen from '@/mixins/screen'

export default {
  mixins: [screen],

  props: {
    value: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      showModal: false,
      localDataErrors: [],
      localData: []
    }
  },

  components: {
    vModal,
    DateInput,
    vButton,
    Autocomplete
  },

  methods: {
    validateWorkingHours () {
      const out = []
      this.localData.forEach((row, index) => {
        out.push(null)

        const errObj = {}
        if ((row.date === null && row.weekDay === null) || (row.date === '' && row.weekDay === '') || (typeof row.date === 'undefined' && typeof row.weekDay === 'undefined')) {
          errObj.date = this.$t('components.Profile.required')
          errObj.weekDay = this.$t('components.Profile.required')
        }

        if (row.workEndMin === null || row.workEndMin === '' || typeof row.workEndMin === 'undefined') {
          errObj.workEndMin = this.$t('components.Profile.required')
        }

        if (row.workStartMin === null || row.workStartMin === '' || typeof row.workStartMin === 'undefined') {
          errObj.workStartMin = this.$t('components.Profile.required')
        }

        if (errObj.date || errObj.weekDay || errObj.workEndMin || errObj.workStartMin) {
          out[index] = errObj
        }
      })

      let hasErrors = false
      out.forEach(e => {
        if (e) {
          hasErrors = true
        }
      })

      if (hasErrors) {
        this.localDataErrors = out
        this.$notify({
          type: 'error',
          message: this.$t('errors.code.fillInAllRequiredFields')
        })
      }

      return !hasErrors
    },

    save () {
      if (this.validateWorkingHours()) {
        this.$emit('input', [...this.localData])
        this.showModal = false
      }
    },

    getTime (val) {
      if (val === null) {
        return val
      }
      const date = new Date()
      const hours = Math.trunc(val / 60)
      const minutes = val % 60
      date.setHours(hours)
      date.setMinutes(minutes)
      date.setSeconds(0)
      date.setMilliseconds(0)
      return date
    },

    getNewTime (item) {
      return (new Date(item).getHours() * 60) + new Date(item).getMinutes()
    },

    onWorkStartMinInput (index, e) {
      const workingHours = [...(this.localData.length ? this.localData : this.value)]
      workingHours[index].workStartMin = e !== null ? this.getNewTime(e) : e
      this.localData = workingHours
      this.localDataErrors = []
    },
    onWeekDayInput (index, e) {
      this.localData[index].weekDay = e
      this.localDataErrors = []
    },
    onWorkEndMinInput (index, e) {
      this.localData[index].workEndMin = e !== null ? this.getNewTime(e) : e
      this.localDataErrors = []
    },
    onDateInput (index, e) {
      this.localData[index].date = e !== null ? new Date(e).toISOString() : e
      this.localDataErrors = []
    },

    removeItem (index) {
      this.localData.splice(index, 1)
      this.localDataErrors = []
    },

    addWorkingHour () {
      this.localData = [...this.localData, {
        date: null,
        id: null,
        weekDay: null,
        workEndMin: null,
        workStartMin: null
      }]
      this.localDataErrors = []
    }
  },

  computed: {
    computedMinWidth () {
      if (this.screen.breakPoint.tabLand) {
        return '90rem'
      }

      if (this.screen.breakPoint.tabPort) {
        return '55rem'
      }

      return null
    },
    computedWorkingHours () {
      if (this.localData.length > 0) {
        return this.localData.map(item => {
          const res = Object.assign({}, item)
          res.workStartMin = this.getTime(res.workStartMin)
          res.workEndMin = this.getTime(res.workEndMin)
          return res
        })
      }

      return []
    },

    weekDays () {
      return [
        {
          label: this.$t('general.weekDays.nominative.Sunday'),
          value: 7
        },
        {
          label: this.$t('general.weekDays.nominative.Monday'),
          value: 1
        },
        {
          label: this.$t('general.weekDays.nominative.Tuesday'),
          value: 2
        },
        {
          label: this.$t('general.weekDays.nominative.Wednesday'),
          value: 3
        },
        {
          label: this.$t('general.weekDays.nominative.Thursday'),
          value: 4
        },
        {
          label: this.$t('general.weekDays.nominative.Friday'),
          value: 5
        },
        {
          label: this.$t('general.weekDays.nominative.Saturday'),
          value: 6
        }
      ]
    }
  },

  watch: {
    showModal (val) {
      if (!val) {
        this.localData = []
      } else {
        this.localData = this.value.map((el) => {
          return Object.assign({}, el)
        })
      }
    }
  }
}
</script>

<style lang="scss">
.add-shop-working-hours {
  &__options-block {
    &:last-child {
      margin-bottom: 2rem;
    }
  }

  &__options-item {

    &:not(:first-child) {
      padding-top: 2.5rem;
    }

    padding-bottom: 2.5rem;

    &:not(:last-child) {
      border-bottom: 1px solid $color-azure-light;
    }

    & > * {
      display: flex;
      flex-direction: column;

      @include respond(tab-land) {
        flex-direction: row;
        justify-content: space-between;
      }

      & > * {
        //margin-right: auto;
        width: 100%;

        &:not(:last-child) {
          margin-bottom: 1.2rem;
          margin-right: 1rem;
        }
      }

    }
  }
}
</style>
